import Dexie from 'dexie';
import {useLiveQuery} from 'dexie-react-hooks';
import db from '../db';

const RECENT_THREADS_LIMIT = 100;

/** `COALESCE(lastMessageAt, lastActivityAt)` — how ThreadsQuery::listRecent orders. */
const recencyOf = (thread) => String(thread.lastMessageAt ?? thread.lastActivityAt ?? '');

const hasEntity = (thread) => !!thread.entityModel && !!thread.entityId;

/**
 * One compound-index lookup: conversations sync whole, so nothing on these paths
 * may read a thread's history to find its last line.
 */
const newestMessage = (threadId) => db.messages
    .where('[threadId+sentAt]')
    .between([threadId, Dexie.minKey], [threadId, Dexie.maxKey])
    .last();

const withNewestMessage = async (thread) => {
    const newest = await newestMessage(thread.id);

    return {
        ...thread,
        messages: newest ? [newest] : [],
        unreadCount: thread.unreadCount ?? 0,
    };
};

/**
 * What `/messages/threads/recent` returns. A thread with no messages is excluded
 * the way the API's join excludes it, settled by `lastMessageAt` alone so the
 * messages table stays shut.
 */
const buildRecentThreads = async (limit = RECENT_THREADS_LIMIT) => {
    const threads = await db.threads.toArray();

    const withMessages = await Promise.all(
        threads
            .filter((thread) => hasEntity(thread) && thread.lastMessageAt)
            .sort((a, b) => recencyOf(b).localeCompare(recencyOf(a)))
            .map(withNewestMessage),
    );

    // Filtered before the window, not after: a thread whose message row has not
    // landed yet would otherwise take a slot and then be dropped, returning fewer
    // rows than the API for the same data.
    return {recent: withMessages.filter((thread) => thread.messages.length > 0).slice(0, limit)};
};

/**
 * What `/messages/threads/unread` returns. The count rides in on the thread, so
 * this is an index range rather than a walk of every message on the device.
 */
const buildUnreadThreads = async () => {
    const threads = await db.threads.where('unreadCount').above(0).toArray();

    const unread = await Promise.all(
        threads
            .filter(hasEntity)
            .sort((a, b) => String(b.lastActivityAt ?? '').localeCompare(String(a.lastActivityAt ?? '')))
            .map(withNewestMessage),
    );

    return {unread};
};

/**
 * `sentAt ?? loggedAt ?? updatedAt ?? recipientUpdatedAt ?? 0` — the key
 * MessagesQuery::addItemsToMessages sorts a conversation on.
 */
const timestampOf = (item) => String(
    item.sentAt ?? item.loggedAt ?? item.updatedAt ?? item.recipientUpdatedAt ?? 0,
);

const omit = (row, keys) => Object.fromEntries(
    Object.entries(row).filter(([key]) => !keys.includes(key)),
);

/**
 * The feed flattens the three lookups `getLogsForEntity` joins — the notification
 * and the email/SMS statuses — so they have to be nested again here.
 */
const asNotificationLog = (row) => ({
    ...omit(row, ['customerId', 'emailStatus', 'smsStatus', 'notificationDelayedUntil']),
    email: row.emailId ? {id: row.emailId, status: row.emailStatus} : null,
    sms: row.smsId ? {id: row.smsId, status: row.smsStatus} : null,
    notification: {delayedUntil: row.notificationDelayedUntil},
});

const asDeferredNotification = (row) => omit(row, ['customerId']);

const asBroadcast = (row) => omit(row, ['customerId', 'archivedAt']);

/**
 * `getThreadById` folds the four sources in one at a time, but every fold is a
 * stable sort with the same comparator, so one sort over them concatenated in the
 * API's order lands identically — ties included, and ties are the norm here.
 */
const mergeConversation = (messages, logs, deferred, broadcasts) => [
    ...messages,
    ...logs,
    ...deferred,
    ...broadcasts,
].sort((a, b) => timestampOf(a).localeCompare(timestampOf(b)));

/**
 * The conversation `/messages/thread/{id}` serves. Notification logs come newest
 * logged first, as `getLogsForEntity` returns them.
 *
 * `null` means the thread hasn't reached this device yet — distinct from a thread
 * that is simply empty, which the feed sends as a row with no messages.
 */
const buildConversation = async (threadId, customerId, includeNotificationLogs = true) => {
    const thread = await db.threads.get(threadId);

    if (!thread) {
        return null;
    }

    const messages = await db.messages.where('threadId').equals(threadId).sortBy('sentAt');

    if (!includeNotificationLogs) {
        return {...thread, messages};
    }

    const byCustomer = (table) => db[table].where('customerId').equals(customerId).toArray();
    const [logs, deferred, broadcasts] = await Promise.all([
        byCustomer('notificationLogs'),
        byCustomer('deferredNotifications'),
        byCustomer('broadcastRecipients'),
    ]);

    return {
        ...thread,
        messages: mergeConversation(
            messages,
            logs
                .sort((a, b) => String(b.loggedAt ?? '').localeCompare(String(a.loggedAt ?? '')))
                .map(asNotificationLog),
            deferred.map(asDeferredNotification),
            broadcasts.filter(({archivedAt}) => !archivedAt).map(asBroadcast),
        ),
    };
};

const useLiveRecentThreads = (limit = RECENT_THREADS_LIMIT, enabled = true) => {
    return useLiveQuery(
        async () => {
            if (!enabled) {
                return null;
            }
            return buildRecentThreads(limit);
        },
        [limit, enabled],
        null,
    );
};

const useLiveUnreadThreads = (enabled = true) => {
    return useLiveQuery(
        async () => {
            if (!enabled) {
                return null;
            }
            return buildUnreadThreads();
        },
        [enabled],
        null,
    );
};

/**
 * Deliberately no initial value, so the three states stay distinct: `undefined` =
 * not answered yet, `null` = answered and the thread is not on the device, an
 * object = the conversation.
 */
const useLiveConversation = (threadId, customerId, includeNotificationLogs = true, enabled = true) => {
    return useLiveQuery(
        async () => {
            if (!enabled || !threadId || !customerId) {
                return null;
            }
            return buildConversation(threadId, customerId, includeNotificationLogs);
        },
        [threadId, customerId, includeNotificationLogs, enabled],
    );
};

/**
 * Clears the badge without waiting for the seen write to come back round on the
 * next sync; the server bumps `lastActivityAt`, so the authoritative count follows.
 */
const markThreadReadLocally = async (threadId) => {
    if (!threadId) {
        return;
    }
    await db.threads.update(threadId, {unreadCount: 0});
};

export {
    buildRecentThreads,
    buildUnreadThreads,
    buildConversation,
    markThreadReadLocally,
    useLiveRecentThreads,
    useLiveUnreadThreads,
    useLiveConversation,
};
